import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import type { DirectedWizardStep } from '../directedSteps';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import { howMadeMessages as messages } from '../messages/howMadeMessages';
import { DIRECTED_STEP_NAMES } from '../shell/DirectedStepper';
import { directedResultFacts } from '../steps/make/resultRules';
import { useDirectedCastNames } from '../useDirectedCastNames';
import { MADE_SECTIONS, madeSectionId } from './howMadeSections';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for "How it was made"; each part uses its own slice of it */
import styles from './HowMade.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The step list of "How it was made" (design l1 `.hwnav`, l2 tabs on a phone): "Back to the video",
 * then each step with what the artist picked. A click scrolls to the step (`?step=`, so Back works).
 */
const HowMadeNavComponent: React.FC = () => {
  const intl = useIntl();
  const { video, madeStep, openMade, openStep } = useDirectedVideoContext();
  const { named } = useDirectedCastNames();
  // The same charge as the price step of "How it was made": the price minus the credits back.
  const charged = directedResultFacts(video).chargedCredits;
  const noPick = intl.formatMessage(messages.noPick);
  const picks: Partial<Record<DirectedWizardStep, string>> = {
    cast: intl.formatMessage(messages.castPick, { count: video.cast.length }),
    stories: video.story === null ? noPick : named(video.story.title),
    style: video.style === null ? noPick : named(video.style.title),
    script: intl.formatMessage(messages.scriptTitle, {
      shots: video.script?.shots.length ?? 0,
      clips: video.clips.total,
      effects: video.script?.effects.length ?? 0,
    }),
    price: intl.formatMessage(messages.credits, { count: charged }),
  };

  return (
    <nav className={styles.nav} aria-label={intl.formatMessage(messages.navLabel)}>
      <SMLButton className={styles.backButton} onClick={() => openStep(null)}>
        {intl.formatMessage(messages.back)}
      </SMLButton>
      <ol className={styles.navList}>
        {MADE_SECTIONS.map((step, index) => (
          <li key={step}>
            <a
              href={`#${madeSectionId(step)}`}
              className={clsx(styles.navItem, madeStep === step && styles.navItemOn)}
              aria-current={madeStep === step ? 'step' : undefined}
              onClick={(event) => {
                event.preventDefault();
                openMade(step);
              }}
            >
              <span className={styles.navNumber}>{index + 1}</span>
              <span className={styles.navName}>{intl.formatMessage(DIRECTED_STEP_NAMES[step])}</span>
              <span className={styles.navPick}>{picks[step] ?? ''}</span>
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};

export const HowMadeNav = TypedMemo(HowMadeNavComponent);
